/**
 * Documents API — talks to the backend for file management.
 * The actual files live in MinIO (storage room), metadata in PostgreSQL (librarian).
 */
import { api } from './api';

// ─── Types matching what the Documents page expects ─────────

export interface DocFile {
  id: string;
  icon: 'pdf' | 'doc' | 'txt';
  name: string;
  kind: string;
  /** Raw DocumentCategory enum value (kind is its display label). */
  category: string;
  case: string;
  caseId: string | null;
  size: string;
  uploaded: string;
  by: string;
  downloadUrl?: string | null;
  /** ISO timestamp when archived, or null when active. Archive is reversible and is NOT delete. */
  archivedAt?: string | null;
}

export interface DocFolder {
  id: string;
  name: string;
  caseId: string | null;
  count: number;
}

// ─── API response shape ─────────────────────────────────────

interface ApiDocument {
  id: string;
  name: string;
  description: string | null;
  fileKey: string;
  mimeType: string;
  fileSizeBytes: string | number;
  category: string;
  createdAt: string;
  downloadUrl?: string | null;
  case: { id: string; caseNumber: string; caseName: string } | null;
  folder: { id: string; name: string } | null;
  uploadedBy: { id: string; firstName: string; lastName: string } | null;
  archivedAt?: string | null;
}

interface ApiFolder {
  id: string;
  name: string;
  caseId: string | null;
  parentId: string | null;
}

// ─── Transform ──────────────────────────────────────────────

function iconFromMime(mime: string): 'pdf' | 'doc' | 'txt' {
  if (mime.includes('pdf')) return 'pdf';
  if (mime.includes('word') || mime.includes('docx') || mime.includes('doc')) return 'doc';
  return 'txt';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function categoryLabel(cat: string): string {
  const map: Record<string, string> = {
    pleading: 'Pleading', correspondence: 'Correspondence', evidence: 'Evidence',
    brief: 'Brief', opinion: 'Opinion', order: 'Order', judgment: 'Judgment',
    draft: 'Draft', fee_note: 'Fee Note', contract: 'Contract',
    skeleton: 'Skeleton argument', authorities: 'Authorities', bundle: 'Hearing bundle',
    other: 'Other',
  };
  return map[cat] ?? cat;
}

function apiToDocFile(d: ApiDocument): DocFile {
  const bytes = typeof d.fileSizeBytes === 'string' ? parseInt(d.fileSizeBytes, 10) : d.fileSizeBytes;
  return {
    id: d.id,
    icon: iconFromMime(d.mimeType),
    name: d.name,
    kind: categoryLabel(d.category),
    category: d.category,
    case: d.case?.caseNumber ?? '',
    caseId: d.case?.id ?? null,
    size: formatSize(bytes),
    uploaded: new Date(d.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    by: d.uploadedBy ? `${d.uploadedBy.firstName} ${d.uploadedBy.lastName}` : '',
    downloadUrl: d.downloadUrl,
    archivedAt: d.archivedAt ?? null,
  };
}

// ─── API calls ──────────────────────────────────────────────

export async function fetchDocuments(
  caseId?: string,
  search?: string,
  /** true → the Archived view (archived documents ONLY). Omitted/false → the active list. */
  archived?: boolean,
): Promise<DocFile[]> {
  const res = await api.get('/documents', {
    params: {
      limit: 100,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      ...(caseId && { caseId }),
      ...(search && { search }),
      ...(archived ? { archived: 'true' } : {}),
    },
  });
  const payload = res.data?.data ?? res.data;
  const docs: ApiDocument[] = payload.documents ?? [];
  return docs.map(apiToDocFile);
}

export async function fetchDocument(id: string): Promise<DocFile> {
  const res = await api.get(`/documents/${id}`);
  const doc = (res.data as any).data ?? res.data;
  return apiToDocFile(doc as ApiDocument);
}

/**
 * Download a document through the canonical AUTHENTICATED stream endpoint — the same
 * mechanism the preview modal uses. Keeps the object key/storage host server-side (no
 * presigned URL exposed to the browser) and enforces auth + matter access on every byte.
 * The server sets the filename via Content-Disposition; we prefer it, falling back to
 * the supplied name.
 */
export async function downloadDocument(id: string, fallbackName?: string): Promise<void> {
  const res = await api.get<ArrayBuffer>(`/documents/${id}/stream`, { responseType: 'arraybuffer' });
  const mime = (res.headers['content-type'] as string | undefined) ?? 'application/octet-stream';
  const disposition = res.headers['content-disposition'] as string | undefined;
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const name = (match?.[1] ? decodeURIComponent(match[1]) : undefined) ?? fallbackName ?? 'document';

  const blobUrl = URL.createObjectURL(new Blob([res.data], { type: mime }));
  try {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

export async function fetchFolders(caseId?: string): Promise<DocFolder[]> {
  const res = await api.get('/documents/folders', { params: caseId ? { caseId } : {} });
  const folders: ApiFolder[] = (res.data as any).data ?? res.data ?? [];
  return folders.map((f) => ({ id: f.id, name: f.name, caseId: f.caseId, count: 0 }));
}

/**
 * Confidentiality tier for a document.
 *
 * `barrister_only` is what makes counsel's material actually privileged — it resolves to the
 * lead and junior barrister on the matter, and excludes clerks and admins. It requires a
 * matter, because that is where the counsel assignment lives.
 */
export type DocumentPermissionScope = 'case_team' | 'chambers' | 'barrister_only' | 'client_visible';

export async function uploadDocument(
  file: File,
  meta: {
    caseId?: string;
    folderId?: string;
    category?: string;
    hearingId?: string;
    permissionScope?: DocumentPermissionScope;
  },
): Promise<DocFile> {
  const formData = new FormData();
  formData.append('file', file);
  if (meta.caseId) formData.append('caseId', meta.caseId);
  if (meta.folderId) formData.append('folderId', meta.folderId);
  if (meta.category) formData.append('category', meta.category);
  if (meta.hearingId) formData.append('hearingId', meta.hearingId);
  if (meta.permissionScope) formData.append('permissionScope', meta.permissionScope);

  const res = await api.post('/documents', formData);
  const doc = (res.data as any).data ?? res.data;
  return apiToDocFile(doc as ApiDocument);
}

/**
 * Archive a document — REVERSIBLE. Requires `document.archive`.
 * This is not `deleteDocument` below: the file, its versions and its audit trail all survive.
 */
export async function archiveDocument(id: string): Promise<DocFile> {
  const res = await api.post(`/documents/${id}/archive`);
  return apiToDocFile((res.data?.data ?? res.data) as ApiDocument);
}

/** Restore an archived document to the active list. Same `document.archive` permission. */
export async function unarchiveDocument(id: string): Promise<DocFile> {
  const res = await api.post(`/documents/${id}/unarchive`);
  return apiToDocFile((res.data?.data ?? res.data) as ApiDocument);
}

export async function deleteDocument(id: string): Promise<void> {
  await api.delete(`/documents/${id}`);
}

export async function renameDocument(id: string, name: string): Promise<void> {
  await api.patch(`/documents/${id}`, { name });
}

// ─── Document versioning ─────────────────────────────────────

export interface DocumentVersion {
  id: string;
  name: string;
  version: number;
  mimeType: string;
  fileSizeBytes: number | null;
  uploadedBy: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
  isLatest: boolean;
}

export async function fetchDocumentVersions(documentId: string): Promise<DocumentVersion[]> {
  const res = await api.get(`/documents/${documentId}/versions`);
  return (res.data as any).data ?? res.data ?? [];
}

export async function uploadDocumentVersion(parentDocId: string, file: File): Promise<DocFile> {
  const formData = new FormData();
  formData.append('file', file);

  const res = await api.post(`/documents/${parentDocId}/versions`, formData);
  const doc = (res.data as any).data ?? res.data;
  return apiToDocFile(doc as ApiDocument);
}

export async function restoreDocumentVersion(versionId: string): Promise<DocFile> {
  const res = await api.post(`/documents/${versionId}/restore`);
  const doc = (res.data as any).data ?? res.data;
  return apiToDocFile(doc as ApiDocument);
}

export interface SendDocumentByEmailPayload {
  toEmail: string;
  ccEmails?: string[];
  subject: string;
  message?: string;
}

export async function sendDocumentByEmail(
  documentId: string,
  payload: SendDocumentByEmailPayload,
): Promise<{ sentAt: string }> {
  const res = await api.post<{ data: { sentAt: string } }>(`/documents/${documentId}/send-by-email`, payload);
  return res.data.data;
}
